import React from "react";

/**
 * Reusable Privacy Policy content component.
 * Used in both the Privacy Policy page and the Legal tab.
 */
const PrivacyPolicyContent = () => {
  return (
    <div className="">
      <h2 className="text-2xl font-semibold mb-1">Docnote Privacy Policy (&quot;Policy&quot;)</h2>
      <p className="text-sm text-gray-600 mb-6">
        <span className="font-bold">Last Updated:</span> March 3, 2026
      </p>

      <p className="text-base mb-6">
        <span className="font-bold">Docnote Inc.</span> (operating as and referred to in this policy as &quot;we&quot;, &quot;us&quot; and
        &quot;our&quot;) is a health technology company that owns and operates an electronic platform called Docnote. We created Docnote to simplify
        and streamline the process for requesting forms for insured medical services and requesting, delivering and paying for uninsured medical
        services. Our clients are physicians and their patients. To exchange PHI through Docnote, both a physician and the physician&apos;s patient
        must be a Docnote client (&quot;Client&quot;). Both the physician and the patient must be prepared to comply with the terms of use
        (&quot;TOU&quot;) of the platform and related services (together, the &quot;Platform&quot;), as the TOU may be amended from time to time to
        account for changes in the Platform or legal requirements.
      </p>

      <p className="text-base mb-6">
        <span className="font-bold">This Policy.</span> As a health tech company, we understand the importance of protecting patient privacy. This
        Policy describes our privacy practices and along with the TOU, governs our relationship with Clients. This Policy identifies our roles and
        obligations under applicable privacy law and describes the security measures we use to protect the personal information we collect to create
        and manage Client accounts (&quot;Personal Information&quot;) and the personal health information exchanged through the Platform between
        Clients (&quot;PHI&quot;). This Policy does not apply to the information practices of Physicians, specifically how they manage the PHI they
        collect, use, or disclose through the Platform. Patients should refer to their Physician&apos;s privacy policy or contact their
        Physician&apos;s privacy officer for information about those practices.
      </p>

      <p className="text-base mb-6">
        <span className="font-bold">Definitions.</span> In this Policy: unless otherwise defined, the terms &quot;agent&quot;, &quot;collect&quot;,
        &quot;use&quot;, &quot;disclose&quot;, &quot;health information custodian&quot;, &quot;information practices&quot;, &quot;personal
        information&quot; and &quot;personal health information&quot; have the same meaning as in the applicable privacy law, &quot;Physicians&quot;
        means physicians who are Clients, and &quot;Patients&quot; patients who are Clients.
      </p>

      <p className="text-base mb-6">
        <span className="font-bold">Privacy Law.</span> There are a several privacy laws in Canada, some federal and some provincial. When we collect
        Personal Information for our own purposes, more specifically to create and manage Client accounts, the applicable law is the Personal
        Information Protection and Electronic Documents Act (&quot;PIPEDA&quot;). In Ontario, when we use PHI to provide the Platform to Physicians,
        the applicable law is the Personal Health Information Protection Act, 2004 (&quot;PHIPA&quot;) and more particularly, the provisions that
        apply to IT providers and agents of health information custodians.
      </p>

      <h4 className="text-lg font-semibold mb-3">Privacy Law Designations</h4>
      <p className="text-base mb-3">Under PHIPA:</p>
      <ul className="list-disc pl-6 mb-6">
        <li className="text-base mb-2">
          a. Physicians are the health information custodians of PHI and between Docnote and Physicians, Physicians own PHI;
        </li>
        <li className="text-base mb-2">
          b. when delivering IT services that enable Physicians to collect, use and disclose PHI (&quot;ESP Services&quot;), we are an &quot;ESP&quot;
          or electronic services provider;
        </li>
        <li className="text-base mb-2">
          c. and when delivering services that involve management of PHI on behalf of Physicians outside of ESP Services (such as certain
          administrative services), we are an agent of Physicians.
        </li>
      </ul>
      <p className="text-base mb-6">
        When we are acting as an ESP or agent, we are acting as a service provider to and on behalf of Physicians to facilitate their exchange of PHI
        with their Patients and the delivery of applicable medical services.
      </p>

      <h4 className="text-lg font-semibold mb-3">Our Collection, Use, Disclosure of Personal Information &amp; PHI</h4>
      <p className="text-base mb-6">
        We collect Personal Information including name, address, email address and date of birth, to register Patients. Personal Information does not
        include professional information, such as that of Physicians. We collect financial information such as credit card numbers for use of the
        Platform to request and receive or provide, as applicable, uninsured services. We may collect Client preferences ( through cookies as
        described below), settings (time zone and language, for example), and feedback Clients choose to provide. We may use and disclose Personal
        Information to comply with legal obligations, as permitted by law (including for the purpose of due diligence in connection with a sale or
        corporate restructuring, to obtain insurance and to assert our rights or defend against legal proceedings), and for maintenance of the
        Platform. We do not use Personal Information if we can use non-identifiable information for a given purpose.
      </p>

      <h4 className="text-lg font-semibold mb-3">Collection &amp; Use of PHI on behalf of Physicians</h4>
      <p className="text-base mb-6">
        When acting as the agent of a Physician, we will only collect, use and disclose PHI as permitted by the Physician. In accordance with PHIPA,
        when providing ESP Services to a Physician, we will not: (a) access or use PHI except as necessary to provide the services; (b) disclose PHI
        unless required by a law of Ontario or Canada or an authority acting under those laws; and (c) permit our personnel to access and use PHI
        before they have agreed to comply with our obligations as an ESP.
      </p>

      <h4 className="text-lg font-semibold mb-3">Collection &amp; Disclosure of PHI by Physicians</h4>
      <p className="text-base mb-6">
        Physicians collect and use PHI to collect fees for uninsured services and verify Patient identity, specifically to verify that an individual
        is a patient of the Physician by comparing the OHIP number and registration information provided by the individual against information in the
        Physician&apos;s records. Verifying Patient identity is a security measure to ensure that Physicians only collect PHI and provide medical
        services to individuals with whom they have a physician/patient relationship. Physicians collect PHI to process and deliver medical services
        requested by Patients. Physicians are responsible for transferring PHI that they need to retain under their record-keeping obligations from
        Docnote into their Patient records. As above, Physicians are the Custodians of and responsible for the management of the PHI that they receive
        and deliver through the Platform, including for obtaining any consent or giving any notice required. We will not have custody or control of
        PHI and as between Physicians and us, PHI is the property of Physicians. Physicians are the contact and responsible for responding to inquires
        or concerns about their management of PHI and requests for access and correction of that PHI. Physicians are the contact in regard to any
        privacy breaches and responsible for providing any breach notification and breach management required of Custodians (see below for our
        notification obligations if we suffer a breach).
      </p>

      <h4 className="text-lg font-semibold mb-3">Representatives</h4>
      <p className="text-base mb-6">
        Patients with legal authority to act for an individual who is unable to use the Platform directly (&quot;Representatives&quot;) may use the
        Platform on the individual&apos;s behalf. Representatives are required to attest that they have legal authority to make decisions regarding
        the individual&apos;s PHI. It is up to Physicians to decide whether to provide services requested through a Representative. Physicians may
        decline to do so if, for example, they are not satisfied that the Representative has the required authority.
      </p>

      <h4 className="text-lg font-semibold mb-3">Our Use of Cookies</h4>
      <p className="text-base mb-6">
        Our platform does not use cookies or similar technologies that affect, alter, or interfere with a user&apos;s system. Consistent with the
        requirements and fair-information principles outlined in Ontario&apos;s Personal Health Information Protection Act, 2004 (PHIPA) and
        Canada&apos;s Personal Information Protection and Electronic Documents Act ( PIPEDA), any cookies we use are strictly limited to essential,
        session-based functions required for secure access and platform performance. We do not use cookies to collect, track, or store Personal Health
        Information (PHI) or Personally Identifiable Information (PII). We do not employ advertising cookies, analytics cookies, or any persistent
        tracking mechanisms that profile users, monitor activity, or identify individuals.
      </p>

      <h4 className="text-lg font-semibold mb-3">Retention of PHI</h4>
      <p className="text-base mb-6">
        The Platform functions as a tunnel through which Physicians and Patients (directly or through Representatives) exchange PHI. The Platform is
        not an information repository. We hold Patient registration information for so long as the Patient and Patient&apos;s Physician are Clients.
        Except as required by law, we only hold PHI for one year to allow time for Physicians to export PHI to their records, after which we securely
        delete PHI. We securely delete PHI where Physicians terminate their use of Docnote. PHI is held for a 60 day grace period after termination.
      </p>

      <h4 className="text-lg font-semibold mb-3">Third-party Service Providers</h4>
      <p className="text-base mb-6">
        We use third-party service providers to assist us with some of the services we provide, including debit and credit card processing where
        applicable, and information hosting. Some of our third-party service providers operate from outside of Canada, which means that they and the
        information that they process may be subject to laws other than those of Ontario or Canada. Our service providers may be required under those
        laws to provide information that they process to a foreign government or governmental authority including law enforcement agencies.
      </p>

      <h4 className="text-lg font-semibold mb-3">Security</h4>
      <p className="text-base mb-3">
        The Company maintains a robust security program designed to protect all information and systems. Such measures include, without limitation:
      </p>
      <ul className="list-disc pl-6 mb-6">
        <li className="text-base mb-2">Hosting data within Canada on secure, monitored servers.</li>
        <li className="text-base mb-2">
          Implementing technical and administrative access controls, including role-based permissions, multifactor authentication, and regular access
          reviews.
        </li>
        <li className="text-base mb-2">Appointing designated personnel responsible for security oversight and incident management.</li>
        <li className="text-base mb-2">Maintaining documented security policies and procedures, regularly reviewed and updated.</li>
        <li className="text-base mb-2">Providing ongoing security and privacy training to personnel.</li>
        <li className="text-base mb-2">
          Conducting regular scans and monitoring for threats and vulnerabilities, with prompt mitigation of identified risks.
        </li>
        <li className="text-base mb-2">Establishing procedures for detection, investigation, and remediation of security incidents.</li>
        <li className="text-base mb-2">Ensuring physical safeguards to protect facilities, equipment, and information.</li>
        <li className="text-base mb-2">
          Performing periodic audits and assessments to verify the effectiveness of security measures and compliance with applicable laws and
          standards.
        </li>
      </ul>
      <p className="text-base mb-6">
        These measures are implemented to ensure a layered and effective approach to safeguarding information and systems.
      </p>

      <h4 className="text-lg font-semibold mb-3">Disclaimer</h4>
      <p className="text-base mb-6">
        Notwithstanding the safeguards we have implemented, we cannot guarantee the security or error-free transmission of Personal Information or PHI
        through the Platform. No IT system is totally secure from threats and risks including interception, loss, corruption, unauthorized use and
        disclosure, delayed availability, and human error. Clients have a role in protecting information they disclose and collect through the
        Platform, including by keeping their log-in credentials confidential.
      </p>

      <h4 className="text-lg font-semibold mb-3">Notification</h4>
      <p className="text-base mb-6">
        In the event we suffer a privacy breach, we will notify the Physician who is the health information custodian of the affected PHI at the first
        reasonable opportunity. In certain circumstances, it may take us time to investigate whether a privacy breach has or is reasonably likely to
        have occurred. We will notify Clients of any technical issues affecting their use of the Platform and in the event of a change in Docnote
        ownership.
      </p>

      <h4 className="text-lg font-semibold mb-3">Amendments to this Policy</h4>
      <p className="text-base mb-6">
        We reserve the right to amend this Policy at any time including in response to a change in the law, our privacy practices, or the Platform. We
        may but are not obligated to notify you of changes to this Policy by email. Please regularly check the &quot;Last Amended&quot; date at the
        top of this Policy and review any amendments so that you can decide whether to continue to use the Platform. By using the Platform or
        continuing to use the Platform after an amendment to this Policy, we will understand you to have agreed to our management of your information
        as described above or in the amended Policy.
      </p>

      <h4 className="text-lg font-semibold mb-3">Contact</h4>
      <p className="text-base mb-3">
        If you have questions about this Policy or more generally, our privacy practices, please contact us by email at:{" "}
        <a className="font-bold text-docsigna-purple-dark" href="mailto:privacy@docnote.com">
          privacy@docnote.com
        </a>
      </p>
    </div>
  );
};

export default PrivacyPolicyContent;
